import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as PE } from 'react'
import { useNavigate } from 'react-router'
import { AndarRealista, capaDaSala } from '../features/office/AndarRealista.tsx'
import { spriteDo, useManifestoDosMoveis } from '../features/office/moveisSprites.ts'
import { BarraDaChamada } from '../features/office/BarraDaChamada.tsx'
import { EscritorioCelular } from '../features/office/EscritorioCelular.tsx'
import { useCompacto } from '../shell/compacto.ts'
import { useChamada } from '../features/office/chamada.tsx'
import { IconePlanta } from '../features/office/IconePlanta.tsx'
import { CATEGORIAS, Cena, itemDoCatalogo, type Categoria, MODELOS, TABULEIROS, TIPOS_DE_MOVEL, medidas, miniaturaDoMovel, previaDoTabuleiro, tabuleiro, type ModeloId, type Movel, type MovelTipo, type SalaNoMapa, type TabuleiroId } from '../features/office/engine.ts'
import { PredioPorFora, construcaoPara } from '../features/office/PredioPorFora.tsx'
import { useCurrentWorkspace } from '../features/workspace/WorkspaceBar.tsx'
import { DURACAO_ABRIR, DURACAO_FECHAR, Portal } from '../features/office/Portal.tsx'
import { api, apiOrigin } from '../lib/api.ts'
import { PainelDaMarca, type MarcaDoEscritorio } from '../features/office/MarcaDoEscritorio'
import type { Paleta } from '../features/office/paleta'
import { CamadaDaConstrucao, miniaturaDaPlanta, useConstrucao } from '../features/office/Construcao.tsx'
import { MODELOS_DE_PLANTA, plantaVazia, tabuleiroLivre, type ModeloDePlanta, type Planta } from '../features/office/planta.ts'
import '../features/office/office.css'
import '../features/office/andares.css'

/**
 * Escritório (SPEC-20261009-2000, SPEC-20261010-0013, SPEC-20261010-1557): o
 * prédio de onde se entra nas chamadas.
 *
 * Cada andar usa um tabuleiro pronto, reto e na proporção da aba; a rodinha
 * aproxima e, afastando até o fim, o andar vira a planta do prédio inteiro.
 * Empresa grande escolhe o andar e a equipe lá em cima e atravessa um portal
 * até ela. Clicar numa sala entra na chamada sem sair do tabuleiro: os
 * controles ficam na barra de baixo.
 */

export type Pessoa = {
  id: string
  memberId: string | null
  name: string
  convidado: boolean
  avatarUrl: string | null
  hasAvatar: boolean
  avatarVersion: string | null
  bio?: string | null
  /** Gente de exemplo, só para testar o tabuleiro: não está numa chamada de verdade. */
  exemplo?: boolean
}
export type Sala = {
  id: string
  floorId: string
  teamId: string | null
  lot: number
  template: ModeloId | 'central'
  name: string
  accessGroupId: string | null
  accessGroupName: string | null
  podeEntrar: boolean
  pessoas: Pessoa[]
}
export type Andar = { id: string; name: string; position: number; tabuleiro: TabuleiroId }
export type Equipe = {
  id: string
  floorId: string
  name: string
  color: string
  accessGroupId: string | null
  accessGroupName: string | null
  podeEntrar: boolean
}
export type Escritorio = {
  andar: string
  tabuleiro: TabuleiroId
  andares: Andar[]
  equipes: Equipe[]
  moveis: Movel[]
  /** Planta livre do andar (SPEC-20261011-2023), quando o tabuleiro é "livre". */
  planta?: Planta | null
  /** Paleta e logo da empresa (SPEC-20261011-1733). */
  marca?: MarcaDoEscritorio
  podeEditar: boolean
  grupos: { id: string; name: string }[]
  salas: Sala[]
}

const CORES = ['#3f6fe0', '#1f9e8a', '#d9822b', '#8a5be0', '#c2484f', '#2c8fd6', '#4f9d3a', '#d6a21e', '#b0558c'] as const
const corDe = (texto: string) => CORES[[...texto].reduce((a, c) => a + c.charCodeAt(0), 0) % CORES.length] as string
const iniciais = (nome: string) => {
  const p = nome.trim().split(/\s+/)
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p[p.length - 1]?.[0] ?? '') : '')).toUpperCase()
}
export const fotoDe = (p: Pessoa) =>
  p.hasAvatar && p.memberId
    ? `${apiOrigin()}/public/member-avatar/${p.memberId}?v=${encodeURIComponent(p.avatarVersion ?? '')}`
    : p.avatarUrl
const noMapa = (s: Sala, cor: string | null): SalaNoMapa => ({
  id: s.id,
  // a área central não tem caixa própria: o desenho só usa a vaga 0
  lot: s.template === 'central' ? 0 : s.lot,
  template: s.template === 'central' ? 'reuniao' : s.template,
  trancada: s.accessGroupId !== null,
  podeEntrar: s.podeEntrar,
  nome: s.name,
  pessoas: s.pessoas.length,
  cor,
})

/* ----------------------------------------------- gente de exemplo --- */

const NOMES_DE_EXEMPLO = [
  ['Beatriz Lopes', 'Produto'], ['Rafael Duarte', 'Desenvolvimento'], ['Camila Rocha', 'Design'], ['Lucas Ferreira', 'Desenvolvimento'],
  ['Juliana Prado', 'Marketing'], ['Pedro Antunes', 'Vendas'], ['Mariana Costa', 'Atendimento'], ['Gustavo Reis', 'Financeiro'],
  ['Larissa Mota', 'RH'], ['Thiago Nunes', 'Desenvolvimento'], ['Fernanda Alves', 'Marketing'], ['Bruno Teixeira', 'Diretoria'],
  ['Aline Souza', 'Design'], ['Diego Martins', 'Vendas'], ['Patrícia Lima', 'Produto'], ['Henrique Castro', 'Suporte'],
  ['Renata Gomes', 'Atendimento'], ['Vinícius Barros', 'Desenvolvimento'], ['Sofia Ribeiro', 'Marketing'], ['Caio Mendes', 'Suporte'],
  ['Isabela Freitas', 'RH'], ['Otávio Pires', 'Financeiro'], ['Clara Moreira', 'Produto'], ['Mateus Cardoso', 'Vendas'],
] as const
/** Quantos de exemplo em cada vaga: muita gente numa sala, pouca em outra, uma lotada demais. */
const EXEMPLO_POR_VAGA: Record<number, number> = { 0: 7, 1: 3, 2: 11, 4: 1, 5: 2, 8: 1 }
export const comExemplo = (salas: Sala[]): Sala[] => {
  let k = 0
  return salas.map((s) => {
    const n = EXEMPLO_POR_VAGA[s.template === 'central' ? 0 : s.lot] ?? 0
    const extras: Pessoa[] = Array.from({ length: n }, () => {
      const [nome, area] = NOMES_DE_EXEMPLO[k++ % NOMES_DE_EXEMPLO.length] as readonly [string, string]
      return {
        id: `exemplo-${k}`,
        memberId: null,
        name: nome,
        convidado: false,
        avatarUrl: `https://api.dicebear.com/9.x/pixel-art/svg?seed=${encodeURIComponent(nome)}`,
        hasAvatar: false,
        avatarVersion: null,
        bio: area,
        exemplo: true,
      }
    })
    return extras.length ? { ...s, pessoas: [...s.pessoas, ...extras] } : s
  })
}
const CHAVE_EXEMPLO = 'orbind.escritorio.exemplo'
export const exemploGuardado = () => {
  try {
    return localStorage.getItem(CHAVE_EXEMPLO) !== '0'
  } catch {
    return true
  }
}
const CHAVE_VISUAL = 'orbind.escritorio.visual'
type Visual = 'pixel' | 'realista'
const visualGuardado = (): Visual => {
  try {
    return localStorage.getItem(CHAVE_VISUAL) === 'realista' ? 'realista' : 'pixel'
  } catch {
    return 'pixel'
  }
}
const CHAVE_ANDAR = 'orbind.escritorio.andar'
const andarGuardado = () => {
  try {
    return localStorage.getItem(CHAVE_ANDAR)
  } catch {
    return null
  }
}
const tipoDe = (s: Sala) => (s.template === 'central' ? 'A conversa aberta do escritório' : MODELOS[s.template].nome)
const corDaSala = (s: Sala) => (s.template === 'central' ? '#b98a52' : MODELOS[s.template].cor)

const ICONES: Record<Sala['template'], string> = {
  reuniao: 'fa-users',
  time: 'fa-laptop-code',
  estudio: 'fa-palette',
  copa: 'fa-mug-hot',
  chefia: 'fa-briefcase',
  cabine: 'fa-couch',
  central: 'fa-comments',
}
function Rosto({ p, tamanho = 26 }: { p: Pessoa; tamanho?: number }) {
  const foto = fotoDe(p)
  return (
    <span className="esc__rosto" style={{ width: tamanho, height: tamanho, background: corDe(p.name) }} title={p.name}>
      {foto ? <img src={foto} alt="" /> : iniciais(p.name)}
    </span>
  )
}

type Janela = { sala: Sala; x: number; y: number; editar: boolean }
type Enquadre = { fit: number; z: number; ox: number; oy: number }
type Viagem = { fase: 'abrir' | 'fechar'; x: number; y: number } | null
type Foco = { salas: string[]; cor: string } | null
/** Andar que virou planta livre e ainda não tem desenho. */
const PLANTA_VAZIA = plantaVazia(28, 16)
const esperar = (ms: number) => new Promise((r) => window.setTimeout(r, ms))

async function buscarEscritorio(andar: string | null): Promise<Escritorio> {
  return (await api.office.$get({ query: andar ? { andar } : {} })).json() as Promise<Escritorio>
}

export function OfficePage() {
  const qc = useQueryClient()
  const chamada = useChamada()
  const navegar = useNavigate()
  const [andarId, setAndarId] = useState<string | null>(andarGuardado)
  const consulta = useQuery({
    queryKey: ['office', andarId],
    queryFn: () => buscarEscritorio(andarId),
    refetchInterval: 4000,
    placeholderData: (anterior) => anterior,
  })
  const dados = consulta.data
  const chave = ['office', andarId] as const
  const minhaSala = chamada?.sala?.salaId ?? null
  // o andar guardado sumiu (apagado): o servidor devolve o primeiro
  useEffect(() => {
    if (dados && !consulta.isPlaceholderData && dados.andar !== andarId) setAndarId(dados.andar)
  }, [dados, andarId, consulta.isPlaceholderData])
  useEffect(() => {
    if (!andarId) return
    try {
      localStorage.setItem(CHAVE_ANDAR, andarId)
    } catch {
      // sem armazenamento: volta ao primeiro andar na próxima visita
    }
  }, [andarId])

  const [exemplo, setExemplo] = useState(exemploGuardado)
  // só quem administra vê (e liga/desliga) a gente de exemplo
  const comGenteDeExemplo = exemplo && dados?.podeEditar === true
  // modo de edição (SPEC-20261010-0451): só quem administra põe, move, gira, pinta e apaga móveis
  const [editando, setEditando] = useState(false)
  // planta livre (SPEC-20261011-2023): a gaveta tem a construção (paredes, chão, salas) e os móveis
  const [abaGaveta, setAbaGaveta] = useState<'construir' | 'moveis'>('construir')
  const avisarRef = useRef<(t: string) => void>(() => {})
  const livre = dados?.tabuleiro === 'livre'
  const construcaoAtiva = livre && editando && abaGaveta === 'construir'
  const construcao = useConstrucao({
    plantaDoServidor: livre ? (dados?.planta ?? PLANTA_VAZIA) : null,
    ativo: construcaoAtiva,
    salvar: async (planta) => {
      if (!dados) return
      const r = await api.office.floors[':id'].plan.$put({ param: { id: dados.andar }, json: planta })
      if (!r.ok) throw new Error('Não foi possível salvar a planta.')
      qc.setQueryData<Escritorio>(chave, (d) => (d ? { ...d, planta } : d))
      void qc.invalidateQueries({ queryKey: ['office'] })
    },
    avisar: (t) => avisarRef.current(t),
  })
  const plantaAtual = livre ? (construcao.planta ?? PLANTA_VAZIA) : null
  const tab = useMemo(() => (plantaAtual ? tabuleiroLivre(plantaAtual) : tabuleiro(dados?.tabuleiro)), [plantaAtual, dados?.tabuleiro])
  const { largura: LARGURA, altura: ALTURA, ox: OX, oy: OY } = medidas(tab)
  const [escolher, setEscolher] = useState(false)
  const [, setMontado] = useState(0)
  const [painelMarca, setPainelMarca] = useState(false)
  /** Paleta em prévia enquanto o painel de cores está aberto (undefined = a salva). */
  const [previaPaleta, setPreviaPaleta] = useState<Paleta | null | undefined>(undefined)
  const miniaturasPixel = useMemo(() => (editando ? (Object.fromEntries(TIPOS_DE_MOVEL.map((k) => [k, miniaturaDoMovel(k).toDataURL()])) as Record<MovelTipo, string>) : null), [editando])
  const manifestoMoveis = useManifestoDosMoveis()
  const [categoria, setCategoria] = useState<Categoria | 'todos'>('todos')
  const [buscaMovel, setBuscaMovel] = useState('')
  const [movelSel, setMovelSel] = useState<{ id: string; x: number; y: number } | null>(null)
  const [fantasma, setFantasma] = useState<{ kind: MovelTipo; x: number; y: number } | null>(null)
  const arrastoMovel = useRef<
    | { tipo: 'novo'; kind: MovelTipo }
    | { tipo: 'mover'; m: Movel; dx: number; dy: number; x0: number; y0: number; moveu: boolean }
    | null
  >(null)
  const todasAsSalas = useMemo(() => (comGenteDeExemplo ? comExemplo(dados?.salas ?? []) : (dados?.salas ?? [])), [dados, comGenteDeExemplo])
  const salas = useMemo(() => todasAsSalas.filter((s) => s.floorId === dados?.andar), [todasAsSalas, dados?.andar])
  const equipes = dados?.equipes ?? []
  const andares = dados?.andares ?? []
  const andarAtual = andares.find((a) => a.id === dados?.andar) ?? null
  const equipeDe = useMemo(() => new Map(equipes.map((e) => [e.id, e])), [equipes])
  const [perfil, setPerfil] = useState<{ p: Pessoa; sala: string; x: number; y: number } | null>(null)

  const vista = useRef<HTMLDivElement>(null)
  const tela = useRef<HTMLCanvasElement>(null)
  const telaDasPlacas = useRef<HTMLCanvasElement>(null)
  const cena = useRef<Cena | null>(null)
  const [enquadre, setEnquadre] = useState<Enquadre>({ fit: 1, z: 1, ox: 0, oy: 0 })
  const enquadreAtual = useRef(enquadre)
  enquadreAtual.current = enquadre
  const [janela, setJanela] = useState<Janela | null>(null)
  const [sobre, setSobre] = useState<string | null>(null)
  // prévia de quem está na sala sob o mouse, sem entrar (SPEC-20261010-1715)
  const [previa, setPrevia] = useState<{ id: string; x: number; y: number } | null>(null)
  const [painel, setPainel] = useState<'quem' | 'equipes' | 'andares' | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [modo, setModo] = useState<'tabuleiro' | 'planta'>('tabuleiro')
  // cada pessoa escolhe como vê o escritório; não muda nada para os outros (SPEC-20261010-1813)
  const [visual, setVisual] = useState<Visual>(visualGuardado)
  const workspace = useCurrentWorkspace()
  const [viagem, setViagem] = useState<Viagem>(null)
  const [foco, setFoco] = useState<Foco>(null)
  const [editarEquipe, setEditarEquipe] = useState<Equipe | 'nova' | null>(null)
  const arrasto = useRef<{ x: number; y: number; ox: number; oy: number; moveu: boolean } | null>(null)
  // celular (SPEC-20261011-0330): a lista de salas é a tela principal; o tabuleiro abre no "Mapa"
  const celular = useCompacto()
  const [mapaCel, setMapaCel] = useState(false)
  const [folhaCelId, setFolhaCelId] = useState<string | null>(null)
  const toques = useRef(new Map<number, { x: number; y: number }>())
  const pinca = useRef<{ d: number } | null>(null)
  const afastou = useRef({ n: 0, t: 0 })

  const avisar = useCallback((texto: string) => {
    setRecado(texto)
    window.setTimeout(() => setRecado((r) => (r === texto ? null : r)), 2400)
  }, [])
  avisarRef.current = avisar

  /* ------------------------------------------------------------ a cena --- */

  useEffect(() => {
    if (!tela.current) return
    const c = new Cena(tela.current, telaDasPlacas.current)
    cena.current = c
    c.iniciar()
    return () => {
      c.parar()
      cena.current = null
    }
  }, [])

  useEffect(() => {
    if (!cena.current || !dados) return
    cena.current.definirTabuleiro(tab)
    cena.current.definirMoveis(dados.moveis ?? [])
    cena.current.definirSalas(salas.map((s) => noMapa(s, s.teamId ? (equipeDe.get(s.teamId)?.color ?? null) : null)))
    // os lugares da área central só existem depois de o andar ser montado: redesenha as pessoas
    setMontado((n) => n + 1)
  }, [dados, salas, equipeDe, tab])
  useEffect(() => {
    if (cena.current) cena.current.destaque = sobre
  }, [sobre])
  const realistaPronto = visual === 'realista' && !!tab.realista
  const blocosPronto = visual === 'pixel' && !!tab.blocos
  const conjMoveis = realistaPronto ? 'realista' : blocosPronto ? 'blocos' : null
  useEffect(() => {
    if (!cena.current) return
    cena.current.semFundo = realistaPronto || blocosPronto
    cena.current.semPlacas = realistaPronto
    cena.current.moveisPorFora = (realistaPronto || blocosPronto) && !!manifestoMoveis
  }, [realistaPronto, blocosPronto, manifestoMoveis])
  /** Desenho do móvel na gaveta: a imagem do Blender no visual de agora, ou o pixel antigo. */
  const miniatura = (k: MovelTipo) => {
    const sp = conjMoveis ? spriteDo(manifestoMoveis, conjMoveis, k) : null
    return sp ? sp.src(0) : (miniaturasPixel?.[k] ?? '')
  }
  const semAcento = (x: string) => x.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
  const termo = semAcento(buscaMovel.trim())
  const moveisDaGaveta = TIPOS_DE_MOVEL.filter((k) => {
    const it = itemDoCatalogo(k)
    if (termo) return semAcento(it.nome).includes(termo)
    return categoria === 'todos' || it.cat === categoria
  })

  /* ---------------------------------------- caber na aba, sem cortar --- */

  const prender = useCallback((e: Enquadre): Enquadre => {
    const el = vista.current
    if (!el) return e
    const vw = el.clientWidth
    const vh = el.clientHeight
    const s = e.fit * e.z
    const w = LARGURA * s
    const h = ALTURA * s
    const ox = w <= vw ? (vw - w) / 2 : Math.min(0, Math.max(vw - w, e.ox))
    const oy = h <= vh ? (vh - h) / 2 : Math.min(0, Math.max(vh - h, e.oy))
    return { ...e, ox, oy }
  }, [LARGURA, ALTURA])

  useLayoutEffect(() => {
    const el = vista.current
    if (!el) return
    const medir = () => {
      const fit = Math.min(el.clientWidth / LARGURA, el.clientHeight / ALTURA)
      setEnquadre((e) => prender({ ...e, fit, z: 1 }))
    }
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    return () => ro.disconnect()
  }, [prender, LARGURA, ALTURA, modo])

  useEffect(() => {
    const el = vista.current
    if (!el || modo !== 'tabuleiro') return
    const roda = (e: WheelEvent) => {
      if ((e.target as HTMLElement).closest('.esc__janela, .esc__lista, .chm-barra, .esc__menu')) return
      e.preventDefault()
      const a = enquadreAtual.current
      // já está vendo o andar inteiro e continua afastando: vira a planta do prédio
      if (e.deltaY > 0 && a.z <= 1.001) {
        const agora = performance.now()
        afastou.current = { n: agora - afastou.current.t < 700 ? afastou.current.n + 1 : 1, t: agora }
        if (afastou.current.n >= 3) {
          afastou.current = { n: 0, t: 0 }
          setJanela(null)
          setPainel(null)
          setModo('planta')
        }
        return
      }
      const r = el.getBoundingClientRect()
      const cx = e.clientX - r.left
      const cy = e.clientY - r.top
      setEnquadre((q) => {
        const nz = Math.max(1, Math.min(10, q.z * (e.deltaY < 0 ? 1.18 : 1 / 1.18)))
        const z = nz < 1.02 ? 1 : nz
        return prender({ fit: q.fit, z, ox: cx - (cx - q.ox) * (z / q.z), oy: cy - (cy - q.oy) * (z / q.z) })
      })
    }
    el.addEventListener('wheel', roda, { passive: false })
    return () => el.removeEventListener('wheel', roda)
  }, [prender, modo])

  const escala = enquadre.fit * enquadre.z
  const trocarVisual = (v: Visual) => {
    setVisual(v)
    try {
      localStorage.setItem(CHAVE_VISUAL, v)
    } catch {
      // sem armazenamento: vale só nesta visita
    }
    if (v === 'realista' && !tab.realista) avisar('O visual realista deste andar ainda está sendo feito. Por enquanto ele fica em pixel art.')
  }
  const centralizarNoCelular = () => {
    const el = vista.current
    if (!el) return
    setEnquadre((q) => {
      const z = el.clientHeight > el.clientWidth ? 2.2 : 1
      const s = q.fit * z
      return prender({ fit: q.fit, z, ox: el.clientWidth / 2 - (LARGURA * s) / 2, oy: el.clientHeight / 2 - (ALTURA * s) / 2 })
    })
  }
  // etiquetas e bolinhas encolhem quando o mapa fica pequeno, sem cobrir as salas
  const inv = Math.max(0.5, Math.min(1, escala / 2.2)) / escala

  /* --------------------------------------------- viajar pelo prédio --- */

  /**
   * Atravessa o portal até um andar e, se pedir, aproxima numa equipe ou numa
   * sala. O andar novo é buscado enquanto o portal cobre a tela.
   */
  const viajar = async (floorId: string, alvo: { equipeId?: string; salaId?: string }, ponto?: { x: number; y: number }) => {
    if (viagem) return
    const r = vista.current?.getBoundingClientRect()
    setJanela(null)
    setPainel(null)
    setEditando(false)
    setViagem({ fase: 'abrir', x: (ponto?.x ?? (r ? r.left + r.width / 2 : 0)) - (r?.left ?? 0), y: (ponto?.y ?? (r ? r.top + r.height / 2 : 0)) - (r?.top ?? 0) })
    try {
      await Promise.all([qc.fetchQuery({ queryKey: ['office', floorId], queryFn: () => buscarEscritorio(floorId) }), esperar(DURACAO_ABRIR)])
    } catch {
      setViagem(null)
      avisar('Não foi possível abrir este andar.')
      return
    }
    setAndarId(floorId)
    setModo('tabuleiro')
    const novos = qc.getQueryData<Escritorio>(['office', floorId])
    const eq = alvo.equipeId ? novos?.equipes.find((e) => e.id === alvo.equipeId) : undefined
    const lista = alvo.salaId ? [alvo.salaId] : eq ? (novos?.salas.filter((s) => s.teamId === eq.id).map((s) => s.id) ?? []) : []
    setFoco(lista.length ? { salas: lista, cor: eq?.color ?? '#ffb020' } : null)
    setViagem({ fase: 'fechar', x: 0, y: 0 })
    window.setTimeout(() => setViagem(null), DURACAO_FECHAR)
  }

  // chegou pelo portal numa equipe ou sala: aproxima nela e acende as salas
  useEffect(() => {
    const c = cena.current
    const el = vista.current
    if (!foco || !c || !el || !dados) return
    const alvo = salas.filter((s) => foco.salas.includes(s.id))
    if (!alvo.length) return
    const rets = alvo.flatMap((s) => {
      const r = c.retanguloDaSala(noMapa(s, null))
      return r ? [r] : []
    })
    if (rets.length) {
      const x0 = Math.min(...rets.map((r) => r[0]))
      const y0 = Math.min(...rets.map((r) => r[1]))
      const x1 = Math.max(...rets.map((r) => r[2]))
      const y1 = Math.max(...rets.map((r) => r[3]))
      setEnquadre((q) => {
        const z = Math.max(1, Math.min(3.2, Math.min(el.clientWidth / ((x1 - x0) * 1.5 * q.fit), el.clientHeight / ((y1 - y0) * 1.6 * q.fit))))
        const s = q.fit * z
        return prender({ fit: q.fit, z, ox: el.clientWidth / 2 - ((x0 + x1) / 2) * s, oy: el.clientHeight / 2 - ((y0 + y1) / 2) * s })
      })
    }
    c.equipeAcesa = { salas: new Set(foco.salas), cor: foco.cor }
    const t = window.setTimeout(() => {
      if (cena.current) cena.current.equipeAcesa = null
      setFoco(null)
    }, 2600)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foco, salas])

  /* ---------------------------------------------------------- ações --- */

  const falhou = (e: unknown) => avisar(e instanceof Error ? e.message : 'Não foi possível salvar.')
  const invalidar = () => void qc.invalidateQueries({ queryKey: ['office'] })
  /** Entrar numa sala é entrar na chamada dela, olhando o tabuleiro (SPEC-20261010-1557). */
  const entrarNaSala = (s: Sala) => {
    setJanela(null)
    void chamada?.entrar({
      id: s.id,
      name: s.name,
      template: s.template,
      exemplo: s.pessoas.filter((p) => p.exemplo).map((p) => ({ nome: p.name, foto: fotoDe(p) })),
    })
  }
  const editar = useMutation({
    mutationFn: async ({ id, ...json }: { id: string; name?: string; accessGroupId?: string | null; teamId?: string | null }) =>
      (await api.office.rooms[':id'].$patch({ param: { id }, json })).json(),
    onSuccess: () => {
      avisar('Sala salva')
      setJanela(null)
      invalidar()
    },
    onError: falhou,
  })

  /* ---------------------------------------------------------- móveis --- */

  const mudarMoveis = (fn: (l: Movel[]) => Movel[]) => qc.setQueryData<Escritorio>(chave, (d) => (d ? { ...d, moveis: fn(d.moveis ?? []) } : d))
  const salvarMovel = async (acao: () => Promise<unknown>) => {
    try {
      await acao()
    } catch (e) {
      falhou(e)
    }
    invalidar()
  }
  const pontoNoChao = (e: { clientX: number; clientY: number }) => {
    const r = tela.current?.getBoundingClientRect()
    if (!r || !cena.current) return null
    return cena.current.chaoEm((e.clientX - r.left) / escala, (e.clientY - r.top) / escala)
  }
  const porMovel = (kind: MovelTipo, e: { clientX: number; clientY: number }) => {
    const p = pontoNoChao(e)
    if (!p || !cena.current?.dentroDoPredio(p[0], p[1]) || !dados) {
      avisar('Solte o móvel dentro do escritório')
      return
    }
    const c = itemDoCatalogo(kind)
    const x = Math.round((p[0] - c.w / 2) * 10) / 10
    const y = Math.round((p[1] - c.d / 2) * 10) / 10
    const temp: Movel = { id: `novo-${Date.now()}`, kind, x, y, variant: 'n', color: null }
    mudarMoveis((l) => [...l, temp])
    void salvarMovel(() => api.office.items.$post({ json: { kind, x, y, floorId: dados.andar } }))
  }
  const atualizarMovel = (id: string, json: { x?: number; y?: number; variant?: 'n' | 's'; color?: string | null }) => {
    mudarMoveis((l) => l.map((m) => (m.id === id ? { ...m, ...json } : m)))
    void salvarMovel(() => api.office.items[':id'].$patch({ param: { id }, json }))
  }
  const apagarMovel = (id: string) => {
    setMovelSel(null)
    mudarMoveis((l) => l.filter((m) => m.id !== id))
    void salvarMovel(() => api.office.items[':id'].$delete({ param: { id } }))
  }
  useEffect(() => {
    if (cena.current) cena.current.movelMarcado = movelSel?.id ?? null
  }, [movelSel, editando])
  // arrastar um móvel novo da gaveta: o ponteiro fica preso no cartão do móvel
  const arrastarDaGaveta = (kind: MovelTipo) => (e: PE<HTMLButtonElement>) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    arrastoMovel.current = { tipo: 'novo', kind }
    setFantasma({ kind, x: e.clientX, y: e.clientY })
  }
  const moverFantasma = (e: PE<HTMLButtonElement>) => {
    if (arrastoMovel.current?.tipo === 'novo') setFantasma((f) => (f ? { ...f, x: e.clientX, y: e.clientY } : f))
  }
  const soltarDaGaveta = (e: PE<HTMLButtonElement>) => {
    const a = arrastoMovel.current
    arrastoMovel.current = null
    setFantasma(null)
    if (a?.tipo !== 'novo') return
    const sobre = document.elementFromPoint(e.clientX, e.clientY)
    if (sobre?.closest('.esc__gaveta')) return
    porMovel(a.kind, e)
  }

  /* ----------------------------------------------- andares e equipes --- */

  const novoAndar = async () => {
    try {
      const n = andares.length + 1
      const r = (await (await api.office.floors.$post({ json: { name: `Andar ${n}`, tabuleiro: 'sede' } })).json()) as { id: string | null }
      invalidar()
      if (r.id) await viajar(r.id, {})
      avisar(`Andar ${n} criado. Troque o tabuleiro dele em "Tabuleiro".`)
    } catch (e) {
      falhou(e)
    }
  }
  const renomearAndar = async (a: Andar, name: string) => {
    try {
      await api.office.floors[':id'].$patch({ param: { id: a.id }, json: { name } })
      invalidar()
    } catch (e) {
      falhou(e)
    }
  }
  const apagarAndar = async (a: Andar) => {
    try {
      await api.office.floors[':id'].$delete({ param: { id: a.id } })
      avisar(`${a.name} apagado`)
      if (a.id === andarId) setAndarId(andares.find((x) => x.id !== a.id)?.id ?? null)
      invalidar()
    } catch (e) {
      falhou(e)
    }
  }

  /* ------------------------------------------------ ponteiro no mapa --- */

  const foraDoMapa = (e: { target: EventTarget }) =>
    (e.target as HTMLElement).closest('.esc__janela, .esc__lista, .esc__barra, .esc__fundo-modal, .esc__gaveta, .chm-barra, .esc__menu, .esc__ver, .escc, .escc-folha, .escc-bolha, .escc-veu, .escc-mapa-topo, .escc-zoom') !== null
  const salaSob = (e: { clientX: number; clientY: number }) => {
    const r = tela.current?.getBoundingClientRect()
    if (!r) return null
    const s = cena.current?.salaEm((e.clientX - r.left) / escala, (e.clientY - r.top) / escala)
    return s ? (salas.find((x) => x.id === s.id) ?? null) : null
  }
  const abrir = (s: Sala, e: { clientX: number; clientY: number }) => {
    const r = vista.current?.getBoundingClientRect()
    setJanela({ sala: s, x: e.clientX - (r?.left ?? 0) + 14, y: e.clientY - (r?.top ?? 0) + 14, editar: false })
    const c = cena.current
    if (!s.podeEntrar && c) {
      c.destaqueNegado = s.id
      window.setTimeout(() => {
        if (c.destaqueNegado === s.id) c.destaqueNegado = null
      }, 900)
    }
  }
  /** Dois dedos no tabuleiro: aproxima e afasta em volta do meio deles (celular e tablet). */
  const zoomEm = (cx: number, cy: number, fator: number) =>
    setEnquadre((q) => {
      const nz = Math.max(1, Math.min(10, q.z * fator))
      const z = nz < 1.02 ? 1 : nz
      return prender({ fit: q.fit, z, ox: cx - (cx - q.ox) * (z / q.z), oy: cy - (cy - q.oy) * (z / q.z) })
    })
  const distanciaDosDedos = () => {
    const [a, b] = [...toques.current.values()]
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0
  }
  const aoApertar = (e: PE<HTMLDivElement>) => {
    if (foraDoMapa(e) || modo !== 'tabuleiro') return
    // o botão direito é do cartão do móvel (aoMenu), nunca entra na chamada
    if (e.pointerType === 'mouse' && e.button === 2) return
    if (!editando) setMovelSel(null)
    if (e.pointerType === 'touch') {
      toques.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (toques.current.size === 2) {
        arrasto.current = null
        pinca.current = { d: distanciaDosDedos() }
        return
      }
    }
    if (construcaoAtiva) {
      const p = pontoNoChao(e)
      const r = vista.current?.getBoundingClientRect()
      if (p && construcao.apertar(p, { x: e.clientX - (r?.left ?? 0) + 14, y: e.clientY - (r?.top ?? 0) + 14 })) return
    }
    if (editando && (!construcaoAtiva || construcao.ferramenta === 'mao')) {
      const r = tela.current?.getBoundingClientRect()
      const m = r && cena.current?.movelEm((e.clientX - r.left) / escala, (e.clientY - r.top) / escala)
      const p = pontoNoChao(e)
      if (m && p) {
        arrastoMovel.current = { tipo: 'mover', m, dx: p[0] - m.x, dy: p[1] - m.y, x0: e.clientX, y0: e.clientY, moveu: false }
        return
      }
      setMovelSel(null)
    }
    arrasto.current = { x: e.clientX, y: e.clientY, ox: enquadre.ox, oy: enquadre.oy, moveu: false }
  }
  /** Botão direito num móvel (quem administra): abre o cartão dele sem entrar na chamada. */
  const aoMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dados?.podeEditar || foraDoMapa(e) || modo !== 'tabuleiro') return
    const r = tela.current?.getBoundingClientRect()
    const m = r && cena.current?.movelEm((e.clientX - r.left) / escala, (e.clientY - r.top) / escala)
    if (!m) return
    e.preventDefault()
    const v = vista.current?.getBoundingClientRect()
    setJanela(null)
    setPrevia(null)
    setSobre(null)
    setMovelSel({ id: m.id, x: e.clientX - (v?.left ?? 0) + 14, y: e.clientY - (v?.top ?? 0) + 14 })
  }
  const aoMover = (e: PE<HTMLDivElement>) => {
    if (modo !== 'tabuleiro') return
    if (e.pointerType === 'touch' && toques.current.has(e.pointerId)) toques.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinca.current) {
      const d = distanciaDosDedos()
      const [a, b] = [...toques.current.values()]
      const r = vista.current?.getBoundingClientRect()
      if (a && b && r && pinca.current.d > 0 && d > 0) zoomEm((a.x + b.x) / 2 - r.left, (a.y + b.y) / 2 - r.top, d / pinca.current.d)
      pinca.current.d = d
      return
    }
    if (construcaoAtiva) {
      const p = pontoNoChao(e)
      if (p && construcao.mover(p)) return
      if (p && !arrasto.current && !foraDoMapa(e)) construcao.pairar(p)
    }
    const am = arrastoMovel.current
    if (am?.tipo === 'mover') {
      if (!am.moveu && Math.hypot(e.clientX - am.x0, e.clientY - am.y0) > 5) {
        am.moveu = true
        setMovelSel(null)
      }
      const p = pontoNoChao(e)
      if (am.moveu && p) {
        const x = Math.round((p[0] - am.dx) * 10) / 10
        const y = Math.round((p[1] - am.dy) * 10) / 10
        mudarMoveis((l) => l.map((m) => (m.id === am.m.id ? { ...m, x, y } : m)))
      }
      return
    }
    const a = arrasto.current
    if (a) {
      const dx = e.clientX - a.x
      const dy = e.clientY - a.y
      if (Math.hypot(dx, dy) > 4) a.moveu = true
      if (a.moveu && (enquadre.z > 1 || celular)) setEnquadre((q) => prender({ ...q, ox: a.ox + dx, oy: a.oy + dy }))
      return
    }
    if (foraDoMapa(e)) return
    if (construcaoAtiva && construcao.ferramenta !== 'mao') return
    if (editando) {
      const r = tela.current?.getBoundingClientRect()
      const m = r && cena.current?.movelEm((e.clientX - r.left) / escala, (e.clientY - r.top) / escala)
      if (vista.current) vista.current.style.cursor = m ? 'grab' : ''
      return
    }
    // com o cartão de um móvel aberto, a prévia da sala não atrapalha
    if (movelSel) return
    const sob = salaSob(e)
    setSobre(sob?.id ?? null)
    if (!sob) setPrevia(null)
    else if (previa?.id !== sob.id) {
      const r = vista.current?.getBoundingClientRect()
      setPrevia({ id: sob.id, x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) })
    }
  }
  const aoSoltar = (e: PE<HTMLDivElement>) => {
    toques.current.delete(e.pointerId)
    if (pinca.current) {
      if (toques.current.size < 2) pinca.current = null
      arrasto.current = null
      return
    }
    if (modo !== 'tabuleiro') return
    if (construcaoAtiva && construcao.soltar()) return
    const am = arrastoMovel.current
    if (am?.tipo === 'mover') {
      arrastoMovel.current = null
      if (!am.moveu) {
        const r = vista.current?.getBoundingClientRect()
        setMovelSel({ id: am.m.id, x: e.clientX - (r?.left ?? 0) + 14, y: e.clientY - (r?.top ?? 0) + 14 })
        return
      }
      const atual = qc.getQueryData<Escritorio>(chave)?.moveis.find((m) => m.id === am.m.id)
      if (atual && cena.current?.dentroDoPredio(atual.x + itemDoCatalogo(atual.kind).w / 2, atual.y + itemDoCatalogo(atual.kind).d / 2))
        atualizarMovel(atual.id, { x: atual.x, y: atual.y })
      else {
        mudarMoveis((l) => l.map((m) => (m.id === am.m.id ? am.m : m)))
        avisar('Solte o móvel dentro do escritório')
      }
      return
    }
    const a = arrasto.current
    arrasto.current = null
    if (!a || a.moveu || editando) return
    const s = salaSob(e)
    if (!s) {
      setJanela(null)
      return
    }
    // um clique entra na chamada da sala (pedido do Cristian, SPEC-20261010-1715); trancada ou
    // a sala em que já estou abre o cartão com quem está lá
    if (celular) {
      setPrevia(null)
      setFolhaCelId(s.id)
    } else if (s.podeEntrar && s.id !== minhaSala) {
      setPrevia(null)
      entrarNaSala(s)
    } else abrir(s, e)
  }

  useEffect(() => {
    const fechar = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setJanela(null)
      setPainel(null)
    }
    window.addEventListener('keydown', fechar)
    return () => window.removeEventListener('keydown', fechar)
  }, [])

  /* ------------------------------------------------------------- telas --- */

  const c = cena.current
  const salaDaChamada = todasAsSalas.find((s) => s.id === minhaSala) ?? null
  const corDaChamada = salaDaChamada ? (salaDaChamada.teamId ? (equipeDe.get(salaDaChamada.teamId)?.color ?? corDaSala(salaDaChamada)) : corDaSala(salaDaChamada)) : '#2fb36d'
  return (
    <div
      className={`esc ob-root${sobre ? ' esc--sobre' : ''}${enquadre.z > 1 ? ' esc--perto' : ''}${editando ? ' esc--editando' : ''}${modo === 'planta' ? ' esc--planta' : ''}${realistaPronto ? ' esc--realista' : ''}${celular && mapaCel ? ' esc--celular' : ''}${construcaoAtiva ? ' esc--construindo' : ''}`}
      ref={vista}
      onPointerCancel={(e) => {
        toques.current.delete(e.pointerId)
        pinca.current = null
        arrasto.current = null
      }}
      onPointerDown={aoApertar}
      onContextMenu={aoMenu}
      onPointerMove={aoMover}
      onPointerUp={aoSoltar}
      onPointerLeave={() => {
        arrasto.current = null
        setSobre(null)
        setPrevia(null)
      }}
    >
      <div className={`esc__chegada${viagem?.fase === 'fechar' ? ' esc__chegada--on' : ''}`} hidden={modo !== 'tabuleiro'}>
        <div className="esc__palco" style={{ transform: `translate(${enquadre.ox}px, ${enquadre.oy}px) scale(${escala})`, ['--inv' as string]: String(inv) }}>
          {realistaPronto || blocosPronto ? (
            <AndarRealista
              conjunto={blocosPronto ? 'blocos' : 'realista'}
              tabuleiro={tab}
              planta={plantaAtual}
              modelos={new Map(salas.filter((s) => s.template !== 'central').map((s) => [s.lot, s.template]))}
              largura={LARGURA}
              altura={ALTURA}
              moveis={dados?.moveis ?? []}
              paleta={previaPaleta !== undefined ? previaPaleta : (dados?.marca?.paleta ?? null)}
              logo={dados?.marca?.logo ? `${apiOrigin()}${dados.marca.logo}` : null}
            />
          ) : null}
          <canvas ref={tela} className="esc__canvas" style={{ width: LARGURA, height: ALTURA }} aria-label="Mapa do andar com as salas e quem está em cada uma" />
          {construcaoAtiva && plantaAtual ? <CamadaDaConstrucao planta={plantaAtual} ox={OX} oy={OY} largura={LARGURA} altura={ALTURA} previa={construcao.previa} /> : null}
          <div className="esc__camada">
            {c
              ? salas.map((s) => {
                  const mapa = noMapa(s, null)
                  return s.pessoas.map((p, i) => {
                    // mais gente que lugares: os últimos não aparecem no tabuleiro, só no cartão e na chamada
                    const pos = c.assento(mapa, i)
                    if (!pos) return null
                    const foto = fotoDe(p)
                    return (
                      <span
                        key={p.id}
                        className="esc__bolinha"
                        style={{ left: pos[0], top: pos[1], background: corDe(p.name), animationDelay: `${-i * 0.6}s` }}
                        onMouseEnter={(e) => {
                          const r = vista.current?.getBoundingClientRect()
                          setPerfil({ p, sala: s.name, x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) })
                        }}
                        onMouseLeave={() => setPerfil((atual) => (atual?.p.id === p.id ? null : atual))}
                      >
                        {foto ? <img src={foto} alt="" /> : iniciais(p.name)}
                      </span>
                    )
                  })
                })
              : null}
          </div>
          <canvas ref={telaDasPlacas} className="esc__placas" style={{ width: LARGURA, height: ALTURA }} aria-hidden />
          {c && realistaPronto ? (
            <div className="esc__camada" aria-hidden>
              {salas.map((s) => {
                const [x, y] = c.etiqueta(noMapa(s, null))
                const cor = s.teamId ? equipeDe.get(s.teamId)?.color : undefined
                return (
                  <span
                    key={s.id}
                    className={`esc__placa-real${sobre === s.id ? ' esc__placa-real--sob' : ''}${s.podeEntrar ? '' : ' esc__placa-real--fechada'}`}
                    style={{ left: x, top: y, ['--equipe' as string]: cor ?? 'transparent' }}
                  >
                    {s.accessGroupId !== null ? <i className={`fas ${s.podeEntrar ? 'fa-lock-open' : 'fa-lock'}`} /> : null}
                    {s.name}
                    {s.pessoas.length ? <b>{s.pessoas.length}</b> : null}
                  </span>
                )
              })}
            </div>
          ) : null}
          {c && !editando ? (
            <div className="esc__camada esc__camada--ver">
              {salas.map((s) => {
                const [x, y] = c.etiqueta(noMapa(s, null))
                return (
                  <button
                    key={s.id}
                    type="button"
                    className="esc__ver"
                    style={{ left: x, top: y + 8 }}
                    title={`Ver quem está em ${s.name} sem entrar`}
                    aria-label={`Ver quem está em ${s.name} sem entrar`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      setPrevia(null)
                      abrir(s, e)
                    }}
                  >
                    <i className="fas fa-eye" aria-hidden />
                    Ver sala
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>
      </div>

      {modo === 'planta' ? (
        <PredioPorFora
          andares={andares}
          salas={todasAsSalas.map((s) => ({
            id: s.id,
            floorId: s.floorId,
            teamId: s.teamId,
            lot: s.lot,
            central: s.template === 'central',
            name: s.name,
            podeEntrar: s.podeEntrar,
            pessoas: s.pessoas.length,
          }))}
          equipes={equipes}
          atual={dados?.andar ?? null}
          minhaSala={minhaSala}
          nome={workspace?.name ?? 'Orbind'}
          onEntrar={(floorId, alvo, ponto) => void viajar(floorId, alvo, ponto)}
        />
      ) : null}

      <header className="esc__barra">
        <div className="esc__chip">
          <h1 className="esc__titulo">Escritório</h1>
          <span className="esc__sub">
            {modo === 'planta' ? 'Visto de fora: clique num andar para entrar.' : 'Clique numa sala para entrar. Afaste com a rodinha para ver o lugar por fora.'}
          </span>
        </div>
        <SeletorDeAndar
          andares={andares}
          atual={dados?.andar ?? null}
          aberto={painel === 'andares'}
          podeEditar={dados?.podeEditar === true}
          emChamada={(id) => todasAsSalas.filter((s) => s.floorId === id).reduce((n, s) => n + s.pessoas.length, 0)}
          onAbrir={() => setPainel((p) => (p === 'andares' ? null : 'andares'))}
          onIr={(a) => (a.id === dados?.andar && modo === 'tabuleiro' ? setPainel(null) : void viajar(a.id, {}))}
          onNovo={() => void novoAndar()}
          onRenomear={(a, n) => void renomearAndar(a, n)}
          onApagar={(a) => void apagarAndar(a)}
        />
        <span className="esc__acoes">
          <button type="button" className={`esc__quem${painel === 'equipes' ? ' esc__quem--on' : ''}`} aria-pressed={painel === 'equipes'} onClick={() => setPainel((p) => (p === 'equipes' ? null : 'equipes'))}>
            <i className="fas fa-layer-group" aria-hidden /> Equipes
          </button>
          <button
            type="button"
            className={`esc__quem${modo === 'planta' ? ' esc__quem--on' : ''}`}
            aria-pressed={modo === 'planta'}
            onClick={() => {
              setJanela(null)
              setPainel(null)
              setEditando(false)
              if (modo === 'planta' && dados) void viajar(dados.andar, {})
              else setModo('planta')
            }}
          >
            <i className={`fas ${construcaoPara(andares.length) === 'casa' ? 'fa-home' : 'fa-building'}`} aria-hidden />{' '}
            {construcaoPara(andares.length) === 'casa' ? 'Casa' : construcaoPara(andares.length) === 'predio' ? 'Prédio' : 'Edifício'}
          </button>
          <span className="esc__visual" role="group" aria-label="Como ver o escritório">
            {(['pixel', 'realista'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={visual === v}
                className={`esc__visual-opcao${visual === v ? ' esc__visual-opcao--on' : ''}`}
                onClick={() => trocarVisual(v)}
              >
                {v === 'pixel' ? 'Pixel' : 'Realista'}
              </button>
            ))}
          </span>
          {dados?.podeEditar && modo === 'tabuleiro' ? (
            <>
              <button type="button" className="esc__quem" onClick={() => setEscolher(true)}>
                <IconePlanta tamanho={16} comSeta={false} /> Tabuleiro
              </button>
              <button
                type="button"
                className={`esc__quem${editando ? ' esc__quem--on' : ''}`}
                aria-pressed={editando}
                onClick={() => {
                  setEditando((v) => !v)
                  setMovelSel(null)
                  setJanela(null)
                  setSobre(null)
                }}
              >
                <i className="fas fa-couch" aria-hidden /> {editando ? 'Concluir' : 'Editar'}
              </button>
              <button
                type="button"
                className={`esc__quem${painelMarca ? ' esc__quem--on' : ''}`}
                aria-pressed={painelMarca}
                onClick={() => {
                  setPainelMarca((v) => !v)
                  setPreviaPaleta(undefined)
                  setPainel(null)
                }}
              >
                <i className="fas fa-palette" aria-hidden /> Cores
              </button>
            </>
          ) : null}
          <button type="button" className={`esc__quem${painel === 'quem' ? ' esc__quem--on' : ''}`} aria-pressed={painel === 'quem'} onClick={() => setPainel((p) => (p === 'quem' ? null : 'quem'))}>
            <i className="fas fa-user-friends" aria-hidden /> Quem está onde
          </button>
        </span>
      </header>

      {painelMarca && !painel && dados?.podeEditar ? (
        <PainelDaMarca
          workspaceId={dados.andar}
          marca={{ paleta: dados.marca?.paleta ?? null, logo: dados.marca?.logo ? `${apiOrigin()}${dados.marca.logo}` : null, logoPropria: dados.marca?.logoPropria ?? false }}
          onPrevia={setPreviaPaleta}
          onSalvar={async (p) => {
            const r = await api.office.palette.$put({ json: p })
            if (!r.ok) throw new Error('falhou')
            const { marca } = (await r.json()) as { marca: MarcaDoEscritorio }
            qc.setQueriesData<Escritorio>({ queryKey: ['office'] }, (d) => (d ? { ...d, marca } : d))
            setPreviaPaleta(undefined)
          }}
          onLogo={(marca) => qc.setQueriesData<Escritorio>({ queryKey: ['office'] }, (d) => (d ? { ...d, marca } : d))}
          onFechar={() => {
            setPainelMarca(false)
            setPreviaPaleta(undefined)
          }}
        />
      ) : null}

      {painel === 'equipes' && dados ? (
        <ListaDeEquipes
          equipes={equipes}
          andares={andares}
          salas={todasAsSalas}
          podeEditar={dados.podeEditar}
          onIr={(e, ev) => void viajar(e.floorId, { equipeId: e.id }, { x: ev.clientX, y: ev.clientY })}
          onNova={() => setEditarEquipe('nova')}
          onEditar={(e) => setEditarEquipe(e)}
        />
      ) : null}

      {painel === 'quem' ? (
        <aside className="esc__lista" aria-label="Quem está onde">
          <header className="esc__lista-topo">
            <h2>Quem está onde</h2>
            {dados?.podeEditar ? (
              <label className="esc__exemplo">
                <input
                  type="checkbox"
                  checked={exemplo}
                  onChange={(e) => {
                    setExemplo(e.target.checked)
                    try {
                      localStorage.setItem(CHAVE_EXEMPLO, e.target.checked ? '1' : '0')
                    } catch {
                      // sem armazenamento: vale só nesta visita
                    }
                  }}
                />
                Pessoas de exemplo (só você vê)
              </label>
            ) : null}
            <span>
              {todasAsSalas.reduce((n, s) => n + s.pessoas.length, 0) || 'Ninguém'}{' '}
              {todasAsSalas.reduce((n, s) => n + s.pessoas.length, 0) === 1 ? 'pessoa em chamada' : todasAsSalas.some((s) => s.pessoas.length) ? 'pessoas em chamada' : 'em chamada agora'}
            </span>
          </header>
          {todasAsSalas.some((s) => s.pessoas.length) ? (
            andares.map((a) => {
              const ocupadas = todasAsSalas.filter((s) => s.floorId === a.id && s.pessoas.length)
              if (!ocupadas.length) return null
              return (
                <div key={a.id}>
                  {andares.length > 1 ? <h3 className="esc__lista-sub">{a.name}</h3> : null}
                  {ocupadas.map((s) => (
                    <section key={s.id} className="esc__grupo" onMouseEnter={() => setSobre(s.id)} onMouseLeave={() => setSobre(null)}>
                      <button
                        type="button"
                        className="esc__grupo-sala"
                        onClick={(e) => (s.floorId === dados?.andar && modo === 'tabuleiro' ? abrir(s, e) : void viajar(s.floorId, { salaId: s.id }, { x: e.clientX, y: e.clientY }))}
                      >
                        <span className="esc__cor" style={{ background: s.teamId ? equipeDe.get(s.teamId)?.color : corDaSala(s) }} />
                        <b>{s.name}</b>
                        {s.accessGroupId ? <i className="fas fa-lock" aria-label="Com cadeado" /> : null}
                        <em>{s.pessoas.length}</em>
                      </button>
                      <ul className="esc__grupo-gente">
                        {s.pessoas.map((p) => (
                          <li key={p.id}>
                            <Rosto p={p} tamanho={22} />
                            {p.name}
                            {p.convidado ? <small>convidado</small> : null}
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )
            })
          ) : (
            <p className="esc__lista-vazia">Entre numa sala: quem chegar depois vê você aqui e entra junto.</p>
          )}
          {salas.some((s) => !s.pessoas.length) ? (
            <>
              <h3 className="esc__lista-sub">Salas livres{andares.length > 1 && andarAtual ? ` · ${andarAtual.name}` : ''}</h3>
              <div className="esc__livres">
                {salas
                  .filter((s) => !s.pessoas.length)
                  .map((s) => (
                    <button key={s.id} type="button" className="esc__livre" onMouseEnter={() => setSobre(s.id)} onMouseLeave={() => setSobre(null)} onClick={(e) => abrir(s, e)}>
                      <span className="esc__cor" style={{ background: s.teamId ? equipeDe.get(s.teamId)?.color : corDaSala(s) }} />
                      {s.name}
                      {s.accessGroupId ? <i className="fas fa-lock" aria-label="Com cadeado" /> : null}
                    </button>
                  ))}
              </div>
            </>
          ) : null}
        </aside>
      ) : null}

      {janela && modo === 'tabuleiro' ? (
        <JanelaDaSala
          key={`${janela.sala.id}-${janela.editar}`}
          janela={janela}
          podeEditar={dados?.podeEditar === true}
          grupos={dados?.grupos ?? []}
          equipes={equipes.filter((e) => e.floorId === janela.sala.floorId)}
          equipe={janela.sala.teamId ? (equipeDe.get(janela.sala.teamId) ?? null) : null}
          capa={janela.sala.template !== 'central' ? capaDaSala(janela.sala.template) : null}
          naChamada={minhaSala === janela.sala.id}
          entrando={chamada?.entrando === janela.sala.id}
          ocupado={editar.isPending}
          onEditar={() => setJanela({ ...janela, editar: true })}
          onFechar={() => setJanela(null)}
          onEntrar={entrarNaSala}
          onSair={() => {
            chamada?.sair()
            setJanela(null)
          }}
          onSalvar={(corpo) => editar.mutate(corpo)}
        />
      ) : null}
      {editando ? (
        <section className={`esc__gaveta${conjMoveis === 'realista' ? ' esc__gaveta--realista' : ''}`} aria-label={livre && abaGaveta === 'construir' ? 'Construção' : 'Móveis'}>
          {livre ? (
            <div className="esc__abas-gaveta" role="tablist" aria-label="O que editar">
              <button type="button" role="tab" aria-selected={abaGaveta === 'construir'} onClick={() => setAbaGaveta('construir')}>
                <i className="fas fa-hammer" aria-hidden /> Construir
              </button>
              <button type="button" role="tab" aria-selected={abaGaveta === 'moveis'} onClick={() => setAbaGaveta('moveis')}>
                <i className="fas fa-couch" aria-hidden /> Móveis
              </button>
            </div>
          ) : null}
          {livre && abaGaveta === 'construir' ? construcao.gaveta : (
          <>
          <div className="esc__gaveta-topo">
            <p>
              <b>Modo criativo.</b> Arraste para o escritório. Clique num móvel para virar, trocar a cor ou apagar.
            </p>
            <label className="esc__gaveta-busca">
              <i className="fas fa-search" aria-hidden />
              <input type="search" value={buscaMovel} onChange={(e) => setBuscaMovel(e.target.value)} placeholder="Procurar móvel ou bichinho" aria-label="Procurar móvel" />
            </label>
          </div>
          <div className="esc__categorias" role="tablist" aria-label="Categorias de móveis">
            {[{ id: 'todos' as const, nome: 'Todos', icone: 'fa-th' }, ...CATEGORIAS].map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={!termo && categoria === c.id}
                className={`esc__categoria${!termo && categoria === c.id ? ' esc__categoria--on' : ''}`}
                onClick={() => {
                  setCategoria(c.id)
                  setBuscaMovel('')
                }}
              >
                <i className={`fas ${c.icone}`} aria-hidden /> {c.nome}
              </button>
            ))}
          </div>
          <div className="esc__catalogo">
            {moveisDaGaveta.length === 0 ? <span className="esc__catalogo-vazio">Nenhum móvel com esse nome. Tente outra palavra.</span> : null}
            {moveisDaGaveta.map((k) => (
              <button
                key={k}
                type="button"
                className={`esc__peca${itemDoCatalogo(k).cat === 'seres' ? ' esc__peca--ser' : ''}`}
                onPointerDown={arrastarDaGaveta(k)}
                onPointerMove={moverFantasma}
                onPointerUp={soltarDaGaveta}
                aria-label={`${itemDoCatalogo(k).nome}: arraste até o escritório`}
              >
                <img src={miniatura(k)} alt="" draggable={false} loading="lazy" />
                <span>{itemDoCatalogo(k).nome}</span>
              </button>
            ))}
          </div>
          </>
          )}
        </section>
      ) : null}
      {construcao.dialogo}
      {fantasma ? (
        <img className="esc__fantasma" src={miniatura(fantasma.kind)} alt="" style={{ left: fantasma.x, top: fantasma.y, width: (itemDoCatalogo(fantasma.kind).w + 1.2) * 16 * escala }} />
      ) : null}
      {movelSel && dados?.podeEditar ? <CartaoDoMovel m={dados?.moveis.find((x) => x.id === movelSel.id) ?? null} x={movelSel.x} y={movelSel.y} onFechar={() => setMovelSel(null)} onMudar={atualizarMovel} onApagar={apagarMovel} /> : null}
      {escolher && dados ? (
        <EscolherTabuleiro
          atual={dados.tabuleiro}
          andar={andarAtual?.name ?? 'este andar'}
          onFechar={() => setEscolher(false)}
          onEscolher={async (id) => {
            await api.office.floors[':id'].$patch({ param: { id: dados.andar }, json: { tabuleiro: id } })
            setEscolher(false)
            avisar(`Tabuleiro trocado para ${tabuleiro(id).nome}`)
            invalidar()
          }}
          onLivre={async (m) => {
            const temDesenho = (dados.tabuleiro === 'livre' && !!dados.planta) || (dados.moveis ?? []).length > 0
            if (temDesenho && !window.confirm(`Montar "${m.nome}" neste andar? O desenho e os móveis de agora saem.`)) return
            try {
              const { planta, moveis } = m.gerar()
              if (dados.tabuleiro !== 'livre') await api.office.floors[':id'].$patch({ param: { id: dados.andar }, json: { tabuleiro: 'livre' } })
              for (const mv of dados.moveis ?? []) await api.office.items[':id'].$delete({ param: { id: mv.id } })
              const r = await api.office.floors[':id'].plan.$put({ param: { id: dados.andar }, json: planta })
              if (!r.ok) throw new Error('Não foi possível montar a planta.')
              for (const mv of moveis) await api.office.items.$post({ json: { kind: mv.kind, x: mv.x, y: mv.y, variant: mv.variant ?? 'n', floorId: dados.andar } })
              setEscolher(false)
              invalidar()
              setAbaGaveta('construir')
              setEditando(true)
              avisar(`${m.nome} montado. Agora é com você: paredes, chão e salas.`)
            } catch (e) {
              falhou(e)
            }
          }}
        />
      ) : null}
      {editarEquipe && dados ? (
        <EditorDeEquipe
          equipe={editarEquipe === 'nova' ? null : editarEquipe}
          andares={andares}
          andarInicial={editarEquipe === 'nova' ? dados.andar : editarEquipe.floorId}
          salas={todasAsSalas}
          grupos={dados.grupos}
          onFechar={() => setEditarEquipe(null)}
          onSalvo={(texto) => {
            setEditarEquipe(null)
            avisar(texto)
            invalidar()
          }}
          onErro={falhou}
        />
      ) : null}
      {previa && !janela && !perfil && modo === 'tabuleiro' && !editando
        ? (() => {
            const s = salas.find((x) => x.id === previa.id)
            if (!s) return null
            const eq = s.teamId ? equipeDe.get(s.teamId) : undefined
            const naChamada = s.id === minhaSala
            return (
              <div className="esc__previa" style={{ left: previa.x + 18, top: previa.y + 18, ['--cor' as string]: eq?.color ?? corDaSala(s) }} role="tooltip">
                <b>{s.name}</b>
                {eq ? <span className="esc__previa-equipe">{eq.name}</span> : null}
                {s.pessoas.length ? (
                  <span className="esc__previa-gente">
                    {s.pessoas.slice(0, 5).map((p) => (
                      <Rosto key={p.id} p={p} tamanho={20} />
                    ))}
                    <small>
                      {s.pessoas
                        .slice(0, 3)
                        .map((p) => p.name.split(' ')[0])
                        .join(', ')}
                      {s.pessoas.length > 3 ? ` e mais ${s.pessoas.length - 3}` : ''}
                    </small>
                  </span>
                ) : (
                  <small>Ninguém aqui agora</small>
                )}
                <em>{naChamada ? 'Você está nesta chamada' : s.podeEntrar ? 'Clique para entrar na chamada' : 'Trancada para você: clique para ver quem está'}</em>
              </div>
            )
          })()
        : null}
      {perfil ? (
        <div className="esc__perfil" style={{ left: perfil.x + 16, top: perfil.y + 16 }} role="tooltip">
          <Rosto p={perfil.p} tamanho={52} />
          <span>
            <b>{perfil.p.name}</b>
            {perfil.p.bio ? <small>{perfil.p.bio}</small> : null}
            <em>
              <i className="fas fa-video" aria-hidden /> Em chamada · {perfil.sala}
            </em>
            {perfil.p.exemplo ? <small className="esc__perfil-exemplo">Pessoa de exemplo</small> : perfil.p.convidado ? <small>Convidado por link</small> : null}
          </span>
        </div>
      ) : null}
      {modo === 'tabuleiro' && enquadre.z > 1.01 && !chamada?.sala && !celular ? <p className="esc__zoom">Zoom {Math.round(enquadre.z * 100)}% · gire a rodinha para voltar</p> : null}
      {modo === 'tabuleiro' && andarAtual && andares.length > 1 ? <p className="esc__placa-andar">{andarAtual.name}</p> : null}
      {celular ? (
        <>
          {mapaCel ? (
            <>
              <div className="escc-mapa-topo">
                <button type="button" className="escc-redondo" aria-label="Voltar para a lista de salas" onClick={() => setMapaCel(false)}>
                  <i className="fas fa-arrow-left" aria-hidden />
                </button>
                <b>{andarAtual?.name ?? 'Escritório'}</b>
                <span className="escc-visual" role="group" aria-label="Como ver o escritório">
                  {(['pixel', 'realista'] as const).map((v) => (
                    <button key={v} type="button" className={visual === v ? 'escc-visual--on' : ''} aria-pressed={visual === v} onClick={() => trocarVisual(v)}>
                      {v === 'pixel' ? 'Pixel' : 'Realista'}
                    </button>
                  ))}
                </span>
              </div>
              <div className="escc-zoom">
                <button type="button" className="escc-redondo" aria-label="Voltar ao centro" onClick={() => centralizarNoCelular()}>
                  <i className="fas fa-crosshairs" aria-hidden />
                </button>
                <button type="button" className="escc-redondo" aria-label="Aproximar" onClick={() => vista.current && zoomEm(vista.current.clientWidth / 2, vista.current.clientHeight / 2, 1.4)}>
                  <i className="fas fa-plus" aria-hidden />
                </button>
                <button type="button" className="escc-redondo" aria-label="Afastar" onClick={() => vista.current && zoomEm(vista.current.clientWidth / 2, vista.current.clientHeight / 2, 1 / 1.4)}>
                  <i className="fas fa-minus" aria-hidden />
                </button>
              </div>
            </>
          ) : null}
          <EscritorioCelular
            salas={salas}
            todasAsSalas={todasAsSalas}
            andares={andares}
            andarAtual={dados?.andar ?? null}
            equipes={equipes}
            minhaSala={minhaSala}
            mapa={mapaCel}
            folha={todasAsSalas.find((x) => x.id === folhaCelId) ?? null}
            fotoDe={fotoDe}
            onFolha={(x) => setFolhaCelId(x?.id ?? null)}
            onAndar={(id) => {
              setAndarId(id)
              setFolhaCelId(null)
            }}
            onEntrar={(x) => {
              setFolhaCelId(null)
              entrarNaSala(x)
            }}
            onAbrirMapa={() => {
              setMapaCel(true)
              window.setTimeout(centralizarNoCelular, 30)
            }}
            onAbrirReuniao={() => navegar('/office/reuniao')}
          />
        </>
      ) : null}
      {chamada?.sala && modo === 'tabuleiro' && !celular ? <BarraDaChamada cor={corDaChamada} onAbrirReuniao={() => navegar('/office/reuniao')} /> : null}
      {viagem ? <Portal fase={viagem.fase} x={viagem.x} y={viagem.y} /> : null}
      {recado ? (
        <p className="esc__recado" role="status">
          {recado}
        </p>
      ) : null}
      {consulta.isError ? <p className="esc__recado esc__recado--erro">Não foi possível carregar o escritório.</p> : null}
    </div>
  )
}

/* ------------------------------------------------------------ andares --- */

/**
 * A escolha do andar lá em cima: com poucos andares, um botão para cada um;
 * com muitos, a lista com busca. Quem administra cria, renomeia e apaga.
 */
function SeletorDeAndar({
  andares,
  atual,
  aberto,
  podeEditar,
  emChamada,
  onAbrir,
  onIr,
  onNovo,
  onRenomear,
  onApagar,
}: {
  andares: Andar[]
  atual: string | null
  aberto: boolean
  podeEditar: boolean
  emChamada: (id: string) => number
  onAbrir: () => void
  onIr: (a: Andar) => void
  onNovo: () => void
  onRenomear: (a: Andar, nome: string) => void
  onApagar: (a: Andar) => void
}) {
  const [busca, setBusca] = useState('')
  const [renomeando, setRenomeando] = useState<string | null>(null)
  const [nome, setNome] = useState('')
  const [apagar, setApagar] = useState<string | null>(null)
  const poucos = andares.length <= 5
  const atualA = andares.find((a) => a.id === atual)
  const filtrados = andares.filter((a) => a.name.toLowerCase().includes(busca.trim().toLowerCase()))
  if (!andares.length) return null
  return (
    <nav className="esc__andares" aria-label="Andares">
      {poucos ? (
        <span className="esc__andares-chips" role="tablist">
          {[...andares].reverse().map((a) => (
            <button key={a.id} type="button" role="tab" aria-selected={a.id === atual} className={`esc__andar${a.id === atual ? ' esc__andar--on' : ''}`} onClick={() => onIr(a)}>
              {a.name}
              {emChamada(a.id) ? <em>{emChamada(a.id)}</em> : null}
            </button>
          ))}
        </span>
      ) : (
        <button type="button" className="esc__andar esc__andar--on" onClick={onAbrir} aria-expanded={aberto}>
          <i className="fas fa-building" aria-hidden /> {atualA?.name ?? 'Andar'}
        </button>
      )}
      {podeEditar || !poucos ? (
        <button type="button" className="esc__andar esc__andar--mais" aria-label="Todos os andares" aria-expanded={aberto} onClick={onAbrir}>
          <i className="fas fa-chevron-down" aria-hidden />
        </button>
      ) : null}
      {aberto ? (
        <div className="esc__menu esc__menu--andares" role="dialog" aria-label="Andares do prédio">
          {andares.length > 6 ? (
            <input className="esc__busca" placeholder="Procurar andar" value={busca} autoFocus onChange={(e) => setBusca(e.target.value)} aria-label="Procurar andar" />
          ) : null}
          <ul>
            {[...filtrados].reverse().map((a) => (
              <li key={a.id} className={a.id === atual ? 'esc__menu-atual' : undefined}>
                {renomeando === a.id ? (
                  <form
                    className="esc__menu-form"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (nome.trim()) onRenomear(a, nome.trim())
                      setRenomeando(null)
                    }}
                  >
                    <input value={nome} maxLength={40} autoFocus onChange={(e) => setNome(e.target.value)} aria-label="Nome do andar" />
                    <button type="submit" className="esc__botao">Salvar</button>
                  </form>
                ) : apagar === a.id ? (
                  <span className="esc__menu-confirma">
                    Apagar {a.name} e as salas dele?
                    <button type="button" className="esc__botao esc__botao--apagar" onClick={() => onApagar(a)}>
                      Apagar
                    </button>
                    <button type="button" className="esc__botao esc__botao--leve" onClick={() => setApagar(null)}>
                      Não
                    </button>
                  </span>
                ) : (
                  <>
                    <button type="button" className="esc__menu-ir" onClick={() => onIr(a)}>
                      <b>{a.name}</b>
                      <small>
                        {tabuleiro(a.tabuleiro).nome}
                        {emChamada(a.id) ? ` · ${emChamada(a.id)} em chamada` : ''}
                      </small>
                    </button>
                    {podeEditar ? (
                      <span className="esc__menu-acoes">
                        <button
                          type="button"
                          className="esc__icone-botao"
                          aria-label={`Renomear ${a.name}`}
                          onClick={() => {
                            setNome(a.name)
                            setRenomeando(a.id)
                          }}
                        >
                          <i className="fas fa-pen" aria-hidden />
                        </button>
                        {andares.length > 1 ? (
                          <button type="button" className="esc__icone-botao" aria-label={`Apagar ${a.name}`} onClick={() => setApagar(a.id)}>
                            <i className="fas fa-trash" aria-hidden />
                          </button>
                        ) : null}
                      </span>
                    ) : null}
                  </>
                )}
              </li>
            ))}
          </ul>
          {podeEditar ? (
            <button type="button" className="esc__menu-novo" onClick={onNovo}>
              <i className="fas fa-plus" aria-hidden /> Novo andar
            </button>
          ) : null}
        </div>
      ) : null}
    </nav>
  )
}

/* ------------------------------------------------------------ equipes --- */

/** A lista de equipes com busca: as suas primeiro; clicar atravessa o portal até ela. */
function ListaDeEquipes({
  equipes,
  andares,
  salas,
  podeEditar,
  onIr,
  onNova,
  onEditar,
}: {
  equipes: Equipe[]
  andares: Andar[]
  salas: Sala[]
  podeEditar: boolean
  onIr: (e: Equipe, ev: { clientX: number; clientY: number }) => void
  onNova: () => void
  onEditar: (e: Equipe) => void
}) {
  const [busca, setBusca] = useState('')
  const termo = busca.trim().toLowerCase()
  const nomeDoAndar = new Map(andares.map((a) => [a.id, a.name]))
  const filtradas = equipes.filter((e) => !termo || e.name.toLowerCase().includes(termo) || (nomeDoAndar.get(e.floorId) ?? '').toLowerCase().includes(termo))
  const minhas = filtradas.filter((e) => e.podeEntrar)
  const outras = filtradas.filter((e) => !e.podeEntrar)
  const linha = (e: Equipe) => {
    const dela = salas.filter((s) => s.teamId === e.id)
    const gente = dela.reduce((n, s) => n + s.pessoas.length, 0)
    return (
      <li key={e.id}>
        <button type="button" className="esc__equipe" style={{ ['--cor' as string]: e.color }} onClick={(ev) => onIr(e, ev)}>
          <span className="esc__equipe-cor" aria-hidden />
          <span className="esc__equipe-nome">
            <b>{e.name}</b>
            <small>
              {nomeDoAndar.get(e.floorId) ?? ''} · {dela.length} {dela.length === 1 ? 'sala' : 'salas'}
              {e.podeEntrar ? '' : ' · só para quem é da equipe'}
            </small>
          </span>
          {gente ? <em title="Em chamada agora">{gente}</em> : null}
          {e.podeEntrar ? null : <i className="fas fa-lock" aria-label="Você não faz parte" />}
        </button>
        {podeEditar ? (
          <button type="button" className="esc__icone-botao" aria-label={`Editar ${e.name}`} onClick={() => onEditar(e)}>
            <i className="fas fa-pen" aria-hidden />
          </button>
        ) : null}
      </li>
    )
  }
  return (
    <aside className="esc__lista esc__equipes" aria-label="Equipes">
      <header className="esc__lista-topo">
        <h2>Equipes</h2>
        <span>{equipes.length ? `${equipes.length} no prédio` : 'Nenhuma ainda'}</span>
      </header>
      {equipes.length > 5 ? <input className="esc__busca" placeholder="Procurar equipe ou andar" value={busca} autoFocus onChange={(e) => setBusca(e.target.value)} aria-label="Procurar equipe" /> : null}
      {minhas.length ? (
        <>
          <h3 className="esc__lista-sub">Suas equipes</h3>
          <ul className="esc__equipes-lista">{minhas.map(linha)}</ul>
        </>
      ) : null}
      {outras.length ? (
        <>
          <h3 className="esc__lista-sub">Outras equipes</h3>
          <ul className="esc__equipes-lista">{outras.map(linha)}</ul>
        </>
      ) : null}
      {!equipes.length ? (
        <p className="esc__lista-vazia">
          {podeEditar ? 'Junte salas de um andar numa equipe: ela ganha uma cor e aparece aqui para todo mundo ir direto até ela.' : 'Quando quem administra criar as equipes, elas aparecem aqui.'}
        </p>
      ) : termo && !filtradas.length ? (
        <p className="esc__lista-vazia">Nenhuma equipe com esse nome.</p>
      ) : null}
      {podeEditar ? (
        <button type="button" className="esc__menu-novo" onClick={onNova}>
          <i className="fas fa-plus" aria-hidden /> Nova equipe
        </button>
      ) : null}
    </aside>
  )
}

/** Criar ou editar uma equipe: nome, cor, quem entra e quais salas do andar são dela. */
function EditorDeEquipe({
  equipe,
  andares,
  andarInicial,
  salas,
  grupos,
  onFechar,
  onSalvo,
  onErro,
}: {
  equipe: Equipe | null
  andares: Andar[]
  andarInicial: string
  salas: Sala[]
  grupos: { id: string; name: string }[]
  onFechar: () => void
  onSalvo: (texto: string) => void
  onErro: (e: unknown) => void
}) {
  const [nome, setNome] = useState(equipe?.name ?? '')
  const [cor, setCor] = useState<string>(equipe?.color ?? CORES[0])
  const [grupo, setGrupo] = useState(equipe?.accessGroupId ?? '')
  const [andar, setAndar] = useState(andarInicial)
  const [marcadas, setMarcadas] = useState<Set<string>>(() => new Set(equipe ? salas.filter((s) => s.teamId === equipe.id).map((s) => s.id) : []))
  const [salvando, setSalvando] = useState(false)
  const [apagar, setApagar] = useState(false)
  const doAndar = salas.filter((s) => s.floorId === andar && s.template !== 'central')
  const alternar = (id: string) =>
    setMarcadas((m) => {
      const n = new Set(m)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const salvar = async () => {
    setSalvando(true)
    try {
      const lista = [...marcadas].filter((id) => doAndar.some((s) => s.id === id))
      if (equipe) await api.office.teams[':id'].$patch({ param: { id: equipe.id }, json: { name: nome.trim(), color: cor as (typeof CORES)[number], accessGroupId: grupo || null, salas: lista } })
      else await api.office.teams.$post({ json: { floorId: andar, name: nome.trim(), color: cor as (typeof CORES)[number], accessGroupId: grupo || null, salas: lista } })
      onSalvo(equipe ? 'Equipe salva' : `Equipe ${nome.trim()} criada`)
    } catch (e) {
      onErro(e)
    } finally {
      setSalvando(false)
    }
  }
  const remover = async () => {
    if (!equipe) return
    try {
      await api.office.teams[':id'].$delete({ param: { id: equipe.id } })
      onSalvo('Equipe apagada; as salas continuam no andar')
    } catch (e) {
      onErro(e)
    }
  }
  return (
    <div className="esc__fundo-modal" role="presentation" onClick={onFechar}>
      <div className="esc__escolha esc__editor-equipe" role="dialog" aria-label={equipe ? `Editar ${equipe.name}` : 'Nova equipe'} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{equipe ? 'Editar equipe' : 'Nova equipe'}</h2>
          <button type="button" className="esc__icone-botao" aria-label="Fechar" onClick={onFechar}>
            <i className="fas fa-times" aria-hidden />
          </button>
        </header>
        <label className="esc__campo">
          Nome
          <input value={nome} maxLength={40} autoFocus onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Desenvolvimento" />
        </label>
        <div className="esc__campo">
          Cor
          <span className="esc__cores" role="radiogroup" aria-label="Cor da equipe">
            {CORES.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={cor === c} className={`esc__cor-bolinha${cor === c ? ' esc__cor-bolinha--on' : ''}`} style={{ background: c }} aria-label={`Cor ${c}`} onClick={() => setCor(c)} />
            ))}
          </span>
        </div>
        <label className="esc__campo">
          Quem entra nas salas da equipe
          <select value={grupo} onChange={(e) => setGrupo(e.target.value)}>
            <option value="">Todo mundo</option>
            {grupos.map((g) => (
              <option key={g.id} value={g.id}>
                Só o grupo {g.name}
              </option>
            ))}
          </select>
        </label>
        {equipe ? null : (
          <label className="esc__campo">
            Andar
            <select
              value={andar}
              onChange={(e) => {
                setAndar(e.target.value)
                setMarcadas(new Set())
              }}
            >
              {andares.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="esc__campo">
          Salas da equipe
          <span className="esc__salas-equipe">
            {doAndar.map((s) => (
              <label key={s.id} className={`esc__sala-opcao${marcadas.has(s.id) ? ' esc__sala-opcao--on' : ''}`} style={{ ['--cor' as string]: cor }}>
                <input type="checkbox" checked={marcadas.has(s.id)} onChange={() => alternar(s.id)} />
                {s.name}
              </label>
            ))}
          </span>
        </div>
        <div className="esc__botoes">
          <button type="button" className="esc__botao" disabled={salvando || !nome.trim()} onClick={() => void salvar()}>
            {equipe ? 'Salvar' : 'Criar equipe'}
          </button>
          <button type="button" className="esc__botao esc__botao--leve" onClick={onFechar}>
            Cancelar
          </button>
          {equipe ? (
            apagar ? (
              <button type="button" className="esc__botao esc__botao--apagar" onClick={() => void remover()}>
                Confirmar: apagar equipe
              </button>
            ) : (
              <button type="button" className="esc__botao esc__botao--leve esc__botao--fim" onClick={() => setApagar(true)}>
                <i className="fas fa-trash" aria-hidden /> Apagar
              </button>
            )
          ) : null}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- salas --- */

function JanelaDaSala({
  janela,
  podeEditar,
  grupos,
  equipes,
  equipe,
  naChamada,
  entrando,
  ocupado,
  onEditar,
  onFechar,
  onEntrar,
  onSair,
  onSalvar,
  capa,
}: {
  /** Foto da sala renderizada no Blender, no alto do cartão. */
  capa?: string | null
  janela: Janela
  podeEditar: boolean
  grupos: { id: string; name: string }[]
  equipes: Equipe[]
  equipe: Equipe | null
  naChamada: boolean
  entrando: boolean
  ocupado: boolean
  onEditar: () => void
  onFechar: () => void
  onEntrar: (s: Sala) => void
  onSair: () => void
  onSalvar: (c: { id: string; name?: string; accessGroupId?: string | null; teamId?: string | null }) => void
}) {
  const s = janela.sala
  const [nome, setNome] = useState(s.name)
  const [grupo, setGrupo] = useState<string>(s.accessGroupId ?? '')
  const [time, setTime] = useState<string>(s.teamId ?? '')
  const caixa = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ left: janela.x, top: janela.y })
  // o cartão nunca sai da tela
  useLayoutEffect(() => {
    const el = caixa.current
    const pai = el?.parentElement
    if (!el || !pai) return
    setPos({
      left: Math.max(10, Math.min(janela.x, pai.clientWidth - el.offsetWidth - 10)),
      top: Math.max(10, Math.min(janela.y, pai.clientHeight - el.offsetHeight - 10)),
    })
  }, [janela.x, janela.y])

  if (janela.editar) {
    return (
      <div ref={caixa} className="esc__janela" style={pos} role="dialog" aria-label={`Editar ${s.name}`}>
        <h3>Editar sala</h3>
        <label className="esc__campo">
          Nome
          <input value={nome} maxLength={40} autoFocus onChange={(e) => setNome(e.target.value)} />
        </label>
        {s.template !== 'central' ? (
          <label className="esc__campo">
            Equipe
            <select value={time} onChange={(e) => setTime(e.target.value)}>
              <option value="">Sem equipe</option>
              {equipes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="esc__campo">
          Quem pode entrar
          <select value={grupo} onChange={(e) => setGrupo(e.target.value)}>
            <option value="">Todo mundo</option>
            {grupos.map((g) => (
              <option key={g.id} value={g.id}>
                Só o grupo {g.name}
              </option>
            ))}
          </select>
        </label>
        <div className="esc__botoes">
          <button
            type="button"
            className="esc__botao"
            disabled={ocupado || !nome.trim()}
            onClick={() => onSalvar({ id: s.id, name: nome.trim(), accessGroupId: grupo || null, ...(s.template !== 'central' ? { teamId: time || null } : {}) })}
          >
            Salvar
          </button>
          <button type="button" className="esc__botao esc__botao--leve" onClick={onFechar}>
            Cancelar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div ref={caixa} className={`esc__janela esc__cartao${s.podeEntrar ? '' : ' esc__janela--negada'}`} style={pos} role="dialog" aria-label={s.name}>
      <header className={`esc__cartao-topo${capa ? ' esc__cartao-topo--foto' : ''}`} style={{ ['--cor' as string]: equipe?.color ?? corDaSala(s) }}>
        {capa ? <img className="esc__cartao-capa" src={capa} alt="" /> : <i className={`fas ${ICONES[s.template]} esc__cartao-marca`} aria-hidden />}
        <div className="esc__cartao-acoes">
          {podeEditar ? (
            <button type="button" className="esc__cartao-acao" aria-label="Editar sala" title="Editar sala" onClick={onEditar}>
              <i className="fas fa-pen" aria-hidden />
            </button>
          ) : null}
          <button type="button" className="esc__cartao-acao" aria-label="Fechar" onClick={onFechar}>
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>
        <div className="esc__cartao-nome">
          <h3>{s.name}</h3>
          <span>
            <i className={`esc__cartao-estado${s.pessoas.length ? ' esc__cartao-estado--ativa' : ''}`} aria-hidden />
            {s.pessoas.length ? `${s.pessoas.length} na chamada agora` : 'Livre agora'}
            {equipe ? ` · ${equipe.name}` : tipoDe(s) !== s.name ? ` · ${tipoDe(s)}` : ''}
          </span>
        </div>
      </header>
      {s.pessoas.length ? (
        <ul className="esc__gente">
          {s.pessoas.map((p) => (
            <li key={p.id}>
              <Rosto p={p} />
              <span>{p.name}</span>
              {p.convidado ? <small>convidado</small> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="esc__vazio">
          <span className="esc__vazio-lugares" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <p>
            <strong>Ninguém aqui ainda</strong>
            Quem entrar primeiro abre a chamada.
          </p>
        </div>
      )}
      {s.accessGroupName && s.podeEntrar ? (
        <p className="esc__cadeado-ok">
          <i className="fas fa-lock" aria-hidden /> Só o grupo {s.accessGroupName}
        </p>
      ) : null}
      {naChamada ? (
        <button type="button" className="esc__entrar esc__entrar--sair" onClick={onSair}>
          <i className="fas fa-phone-slash" aria-hidden /> Sair da chamada
        </button>
      ) : s.podeEntrar ? (
        <button type="button" className="esc__entrar" autoFocus disabled={ocupado || entrando} onClick={() => onEntrar(s)}>
          <i className="fas fa-headset" aria-hidden /> {entrando ? 'Entrando…' : s.pessoas.length ? 'Entrar na chamada' : 'Abrir a chamada'}
        </button>
      ) : (
        <p className="esc__trancada">
          <i className="fas fa-lock" aria-hidden /> {equipe && !equipe.podeEntrar ? `Só quem é da equipe ${equipe.name} entra.` : `Só quem é do grupo ${s.accessGroupName ?? 'desta sala'} entra.`} Você vê quem está aqui, mas não pode entrar.
        </p>
      )}
    </div>
  )
}

/** Escolha do tabuleiro pronto de um andar: miniatura de cada um, com as salas padrão. */
function EscolherTabuleiro({
  atual,
  andar,
  onFechar,
  onEscolher,
  onLivre,
}: {
  atual: TabuleiroId
  andar: string
  onFechar: () => void
  onEscolher: (id: TabuleiroId) => Promise<void>
  onLivre: (m: ModeloDePlanta) => Promise<void>
}) {
  const [montando, setMontando] = useState<string | null>(null)
  const miniaturas = useMemo(() => Object.fromEntries(MODELOS_DE_PLANTA.map((m) => [m.id, miniaturaDaPlanta(m.gerar().planta)])), [])
  const [salvando, setSalvando] = useState<TabuleiroId | null>(null)
  const previas = useMemo(() => Object.fromEntries(TABULEIROS.map((t) => [t.id, previaDoTabuleiro(t.id).toDataURL()])), [])
  return (
    <div className="esc__fundo-modal" role="presentation" onClick={onFechar}>
      <div className="esc__escolha" role="dialog" aria-label="Escolher tabuleiro" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Tabuleiro de {andar}</h2>
          <button type="button" className="esc__icone-botao" aria-label="Fechar" onClick={onFechar}>
            <i className="fas fa-times" aria-hidden />
          </button>
        </header>
        <p>As salas seguem pelas vagas: nome, cadeado, equipe e quem está dentro continuam. Salas que o tabuleiro novo não tem ficam guardadas.</p>
        <div className="esc__tabuleiros">
          {TABULEIROS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`esc__tabuleiro${t.id === atual ? ' esc__tabuleiro--atual' : ''}`}
              disabled={salvando !== null}
              onClick={() => {
                if (t.id === atual) return onFechar()
                setSalvando(t.id)
                void onEscolher(t.id).finally(() => setSalvando(null))
              }}
            >
              <img src={previas[t.id]} alt="" />
              <b>{t.nome}</b>
              <span>{t.descricao}</span>
              {t.id === atual ? <em>Em uso</em> : salvando === t.id ? <em>Trocando…</em> : null}
            </button>
          ))}
        </div>
        <section className="esc__livres">
          <h3>Planta livre: desenhe o seu</h3>
          <p>Comece de um modelo e mude o que quiser no Editar: paredes, janelas, portas, chão e as salas de chamada.{atual === 'livre' ? ' Este andar já é planta livre; escolher um modelo troca o desenho atual.' : ''}</p>
          <div className="esc__tabuleiros">
            {MODELOS_DE_PLANTA.map((m) => (
              <button
                key={m.id}
                type="button"
                className="esc__tabuleiro"
                disabled={montando !== null || salvando !== null}
                onClick={() => {
                  setMontando(m.id)
                  void onLivre(m).finally(() => setMontando(null))
                }}
              >
                <img src={miniaturas[m.id]} alt="" />
                <b>{m.nome}</b>
                <span>{m.descricao}</span>
                {montando === m.id ? <em>Montando…</em> : null}
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

/** Cartão do móvel escolhido no modo de edição: virar, cor e apagar. */
function CartaoDoMovel({
  m,
  x,
  y,
  onFechar,
  onMudar,
  onApagar,
}: {
  m: Movel | null
  x: number
  y: number
  onFechar: () => void
  onMudar: (id: string, json: { variant?: 'n' | 's'; color?: string | null }) => void
  onApagar: (id: string) => void
}) {
  if (!m) return null
  const item = itemDoCatalogo(m.kind)
  return (
    <div className="esc__janela esc__movel" style={{ left: x, top: y }} role="dialog" aria-label={item.nome}>
      <header>
        <b>{item.nome}</b>
        <button type="button" className="esc__icone-botao" aria-label="Fechar" onClick={onFechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </header>
      {item.gira ? (
        <button type="button" className="esc__botao esc__botao--leve" onClick={() => onMudar(m.id, { variant: m.variant === 'n' ? 's' : 'n' })}>
          <i className="fas fa-sync-alt" aria-hidden /> Virar para o outro lado
        </button>
      ) : null}
      {item.cores ? (
        <div className="esc__cores" role="group" aria-label="Cor">
          {item.cores.map((cor) => (
            <button
              key={cor}
              type="button"
              className={`esc__cor-bolinha${(m.color ?? item.cores?.[0]) === cor ? ' esc__cor-bolinha--on' : ''}`}
              style={{ background: cor }}
              aria-label={`Cor ${cor}`}
              onClick={() => onMudar(m.id, { color: cor })}
            />
          ))}
        </div>
      ) : null}
      <button type="button" className="esc__botao esc__botao--apagar" onClick={() => onApagar(m.id)}>
        <i className="fas fa-trash" aria-hidden /> Apagar
      </button>
    </div>
  )
}
